/**
 * CUSTOM_TAXFILING_BY_CLAUDE (KAREN 5ba17d6bcbae, Ryan 2026-10-14)
 * ==============================================================================
 * The "Filing calendar" card on the Tax Hub: how often each registered state files, the returns owed
 * and when they are due, Mark filed (freezes that state's numbers for the period), Ryan's Unlock, and the
 * reminder switch.
 *   GET  /tax-filing                     the calendar (admin, or an agent under taxfiling.read)
 *   POST /tax-filing/schedule            how often a state files + its due day
 *   POST /admin/tax-filing/mark          mark filed (admin)
 *   POST /admin/tax-filing/unlock        unlock (Ryan only, with a reason)
 *   GET  /admin/tax-filing/snapshot      the frozen rows of one filed period
 *   GET|POST /admin/tax-filing-config    the reminder switch + the last run
 *   POST /tax-filing/run                 the filing run draft ({ready} sends it to Ryan)      CUSTOM_TAXFILINGRUN_BY_CLAUDE
 *   POST /tax-filing/run/approve         Ryan approves, or sends it back with a reason
 *   POST /tax-filing/run/confirm         record the submit + the state's confirmation number
 *   POST /admin/tax-filing/confirmation  a confirmation number for a period already marked filed
 *   GET  /tax-filing/guide               every state's "How to file & pay" guide            CUSTOM_TAXFILINGGUIDE_BY_CLAUDE
 *   POST /tax-filing/guide               save a state's steps / video / yearly / notes
 *   POST /admin/tax-filing/guide-accounts  the state account numbers (Ryan only)
 *   GET  /tax-filing/worksheet           the numbers for the guide (when no approved run)
 *
 * Owns #tax-filing-root and nothing else. Also serves `window.TaxFiling.frozenFor(state, from, to)`, which the
 * Sales-by-state report (custom-tax-hub.js) asks so a filed period shows its frozen rows. Borrows the nexus
 * cards' `tn-` styles.
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev';
  var PREF = 'taxnexus_collapsed_filing';

  var st = {
    cal: null, calErr: null,
    cfg: null, cfgErr: null,
    edits: {},          // state -> {frequency, dueDay} being changed
    marking: null,      // 'CO|2026-09' whose Mark filed form is open
    busy: false, msg: '', msgErr: false,
    snaps: {},          // 'CO|2026-09' -> {loading} | {rows, rec} | {error}
    runOpen: null,      // 'CO|2026-09' whose filing-run panel is open (CUSTOM_TAXFILINGRUN_BY_CLAUDE)
    proofOpen: null,    // 'CO|2026-09' whose proof panel is open (CUSTOM_TAXFILINGPROOF_BY_CLAUDE)
    proofs: {},         // 'CO|2026-09' -> {loading} | {items} | {error}
    guideOpen: null,    // 'CO|2026-09' whose "How to file & pay" guide is open (CUSTOM_TAXFILINGGUIDE_BY_CLAUDE)
    guides: null,       // {loading} | {byState} | {error}
    ws: {},             // 'CO|2026-09' -> {loading} | worksheet | {error}
    guideEdit: null,    // 'CO|2026-09' whose steps are being edited
    acctEdit: null      // 'CO|2026-09' whose account numbers Ryan is editing
  };

  function api(path, opts) {
    if (!(window.RepPortal && window.RepPortal.API && window.RepPortal.API.fetchExternal)) {
      return Promise.reject(new Error('Portal API helper unavailable. Reload the page.'));
    }
    return window.RepPortal.API.fetchExternal(WORKER_URL, path, opts || { method: 'GET' });
  }
  function session() {
    if (window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession) return window.RepPortal.Auth.getSession();
    try { var raw = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session'); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }
  function isRyan() {
    var s = session() || {};
    return !!s.admin && String(s.repCode || s.code || s.initials || '').toUpperCase() === 'RW';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  function usd(n) { var v = Number(n) || 0; return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function collapsed() { try { return localStorage.getItem(PREF) === '1'; } catch (e) { return false; } }
  function setCollapsed(v) { try { localStorage.setItem(PREF, v ? '1' : '0'); } catch (e) {} }
  function rootEl() { return document.getElementById('tax-filing-root'); }
  function mdY(iso) {
    if (!iso) return '';
    var p = String(iso).slice(0, 10).split('-');
    return p.length === 3 ? Number(p[1]) + '/' + Number(p[2]) + '/' + p[0] : esc(iso);
  }
  function errOf(err) { var m = (err && err.message) || 'Request failed.'; return /403|admins only/i.test(m) ? 'Admins only.' : m; }

  var FREQ = [['', 'Not set'], ['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['annual', 'Yearly']];

  function statusPill(p) {
    if (p.status === 'filed') return '<span class="tn-pill tn-pill--ok">Filed</span>';
    if (p.status === 'unlocked') return '<span class="tn-pill tn-pill--warn">Unlocked</span>';
    if (p.status === 'overdue') return '<span class="tn-pill tn-pill--bad">Overdue</span><span class="tn-sub">' + (-p.daysLeft) + ' day' + (p.daysLeft === -1 ? '' : 's') + ' late</span>';
    if (p.status === 'due-soon') return '<span class="tn-pill tn-pill--warn">' + (p.daysLeft === 0 ? 'Due today' : p.daysLeft + ' day' + (p.daysLeft === 1 ? '' : 's')) + '</span>';
    return '<span class="tn-pill tn-pill--none">Open</span><span class="tn-sub">' + p.daysLeft + ' days</span>';
  }

  /* CUSTOM_TAXFILINGRUN_BY_CLAUDE (KAREN dc4df6b4dc0d) - the filing run: figures -> Ryan's approval -> filed on the state's
     site -> the state's confirmation number. Nothing here files or pays anything. */
  var RUN_WORDS = { draft: 'Draft', 'awaiting-ryan': 'Waiting on Ryan', approved: 'Approved', submitted: 'Submitted', confirmed: 'Confirmed' };
  var RETURN_TYPES = [['sales', 'Sales tax'], ['use', 'Use tax'], ['sales-and-use', 'Sales and use tax']];
  var PAY_METHODS = [['', 'Not set'], ['ach-debit', 'ACH debit'], ['ach-credit', 'ACH credit'], ['card', 'Card'], ['check', 'Check'], ['none', 'Nothing to pay'], ['other', 'Other']];
  function runPill(run) {
    if (!run) return '<span class="tn-muted">None yet</span>';
    var cls = run.status === 'confirmed' ? 'tn-pill--ok' : run.status === 'approved' || run.status === 'submitted' ? 'tn-pill--ok' : run.status === 'awaiting-ryan' ? 'tn-pill--warn' : 'tn-pill--none';
    var h = '<span class="tn-pill ' + cls + '">' + esc(RUN_WORDS[run.status] || run.status) + '</span>';
    if (run.sentBack && run.status === 'draft') h += '<span class="tn-sub tn-badword">sent back</span>';
    if (run.approval && run.approval.stillMatches === false) h += '<span class="tn-sub tn-badword">changed since approval</span>';
    return h;
  }
  function confCell(f) {
    if (f.confirmationLast4) return '<span class="tn-pill tn-pill--ok">&#10003; Conf &bull;&bull;' + esc(f.confirmationLast4) + '</span>';
    if (f.needsConfirmation) return '<span class="tn-pill tn-pill--warn">&#9888; No confirmation yet</span>'
      + '<button type="button" class="th-btn th-btn--sm" data-tf="add-conf" data-state="' + esc(f.state) + '" data-period="' + esc(f.period) + '">Add #</button>';
    return '<span class="tn-muted">&mdash;</span>';
  }
  /* CUSTOM_TAXFILINGPROOF_BY_CLAUDE (KAREN cdd12ba998e6, Ryan 2026-10-17) - proof a return was filed: the state's receipt
     (a PDF or image) or a reference to the confirmation email the state sent, kept on the filed period. A filed period
     with none shows an amber "No receipt"; it still counts as filed (Ryan: an email on record is enough). */
  function proofCell(f) {
    var p = f.proof || { count: 0 };
    var key = f.state + '|' + f.period;
    var h = p.count
      ? '<span class="tn-pill tn-pill--ok">&#10003; ' + p.count + ' on record</span><span class="tn-sub">' + (p.files || 0) + ' file' + (p.files === 1 ? '' : 's') + ', ' + (p.emails || 0) + ' email' + (p.emails === 1 ? '' : 's') + '</span>'
      : (f.needsProof ? '<span class="tn-pill tn-pill--warn">&#9888; No receipt</span>' : '<span class="tn-muted">&mdash;</span>');
    return h + ' <button type="button" class="th-btn th-btn--sm" data-tf="proof-open" data-key="' + esc(key) + '">' + (p.count ? 'View' : 'Attach') + '</button>';
  }
  function proofPanel(f, key, colspan) {
    var pr = st.proofs[key] || {};
    var h = '<tr class="tn-row--edit" data-tf-proof="' + esc(key) + '"><td colspan="' + colspan + '">';
    h += '<p class="tn-sub">Proof that ' + esc(f.state) + ' ' + esc(f.label || f.period) + ' was filed and paid: the state\'s confirmation page or payment receipt, or the confirmation email the state sent (Karen records those from compliance@).</p>';
    if (pr.loading) h += '<p class="tn-muted">Loading&hellip;</p>';
    else if (pr.error) h += '<p class="tn-err">' + esc(pr.error) + '</p>';
    else if (pr.items && pr.items.length) {
      h += '<ul class="tf-proof-list">';
      pr.items.forEach(function (i) {
        h += '<li><strong>' + esc(i.kindLabel || i.kind) + ':</strong> ';
        if (i.source === 'file') h += esc(i.name) + ' <button type="button" class="th-btn th-btn--sm" data-tf="proof-view" data-key="' + esc(key) + '" data-id="' + esc(i.id) + '">Open</button>';
        else h += 'email "' + esc(i.subject) + '" in ' + esc(i.mailbox) + (i.folder ? ' / ' + esc(i.folder) : '') + ', ' + esc(i.receivedAt || '');
        h += ' <span class="tn-sub">added ' + mdY(i.at) + ' by ' + esc(i.by || '') + (i.note ? ' &middot; ' + esc(i.note) : '') + '</span>';
        h += ' <button type="button" class="th-btn th-btn--sm" data-tf="proof-remove" data-key="' + esc(key) + '" data-id="' + esc(i.id) + '">Remove</button></li>';
      });
      h += '</ul>';
    } else if (pr.items) h += '<p class="tn-muted">Nothing on record yet.</p>';
    h += '<div class="tn-actions"><label class="tn-sub tn-inline">Kind <select class="tn-in" data-f="proof-kind" aria-label="Kind">'
      + opt([['confirmation', 'State confirmation'], ['payment-receipt', 'Payment receipt'], ['other', 'Other']], 'confirmation') + '</select></label> '
      + '<input class="tn-in" type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" data-f="proof-file" aria-label="Receipt file"> '
      + '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="proof-attach" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Attach file</button> '
      + '<button type="button" class="th-btn th-btn--sm" data-tf="proof-close">Close</button></div>';
    return h + '</td></tr>';
  }
  function opt(list, val) { return list.map(function (o) { return '<option value="' + o[0] + '"' + (String(val == null ? '' : val) === o[0] ? ' selected' : '') + '>' + o[1] + '</option>'; }).join(''); }
  function numIn(f, val, label) { return '<label class="tn-sub tn-inline">' + label + ' $ <input class="tn-in" type="number" min="0" step="0.01" data-f="' + f + '" value="' + (val == null ? '' : esc(val)) + '" aria-label="' + label + '"></label>'; }
  /* CUSTOM_TAXUPLOADFILE_BY_CLAUDE (KAREN b5fdf7fcf7ac) - the state's upload file from an approved run (Washington only:
     Colorado waits for its local tax, Florida for Form DR-600001). The worker builds it; this saves it as a file. */
  var UPLOAD_STATES = { WA: 'My DOR' };
  var WA_DEDUCTIONS = [['', 'Why were these sales not taxed?'], ['04', '04 Interstate and Foreign Sales'], ['18', '18 Sales to US Government'],
    ['31', '31 Returns and Allowances'], ['01', '01 Bad Debts'], ['02', '02 Cash and Trade Discounts'], ['99', '99 Other (explain)']];
  function uploadBlock(p, key, run) {
    if (!run || !UPLOAD_STATES[p.state] || ['approved', 'submitted', 'confirmed'].indexOf(run.status) < 0) return '';
    var h = '<div class="tn-actions">';
    if (Number(run.exemptSales) > 0) {
      h += '<label class="tn-sub tn-inline">Deduction <select class="tn-in" data-f="ded" aria-label="Deduction code">' + opt(WA_DEDUCTIONS, '') + '</select></label>'
        + '<input class="tn-in tn-in--note" maxlength="100" data-f="dednote" placeholder="Explanation (code 99 only)" aria-label="Deduction explanation">';
    }
    h += '<button type="button" class="th-btn th-btn--sm" data-tf="upload-file" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Download upload file</button>'
      + '</div><p class="tn-sub">The file for ' + esc(UPLOAD_STATES[p.state]) + ' "Upload a file" on the excise return, made from these approved figures. Upload it, check the return, then submit.</p>';
    return h;
  }
  function downloadUploadFile(key) {
    var parts = key.split('|');
    var q = '?state=' + encodeURIComponent(parts[0]) + '&period=' + encodeURIComponent(parts[1])
      + '&dedCode=' + encodeURIComponent(runVal(key, 'ded')) + '&dedNote=' + encodeURIComponent(runVal(key, 'dednote'));
    st.busy = true; st.msg = ''; render();
    api('/tax-filing/upload-file' + q).then(function (res) {
      st.busy = false;
      try {
        var blob = new Blob([res.text || ''], { type: 'text/csv' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = res.filename || 'upload.csv';
        document.body.appendChild(a); a.click(); setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      } catch (e) { st.msgErr = true; st.msg = 'Could not save the file: ' + errOf(e); render(); return; }
      var off = res.worksheet && res.worksheet.matches === false;
      st.msgErr = !!off;
      st.msg = parts[0] + ' ' + parts[1] + ' upload file saved. ' + (res.checks || []).join(' ');
      render();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = 'No upload file: ' + errOf(err); render(); });
  }
  /* END CUSTOM_TAXUPLOADFILE_BY_CLAUDE */
  function runPanel(p, key, colspan) {
    var run = p.run || null;
    var status = run ? run.status : 'draft';
    var h = '<tr class="tn-row--edit" data-tf-run="' + esc(key) + '"><td colspan="' + colspan + '">';
    if (run) {
      h += '<p><strong>' + esc(run.summary) + '</strong></p>';
      (run.checks || []).forEach(function (c) { h += '<p class="tn-sub tn-warnword">Check: ' + esc(c) + '</p>'; });
      if (run.sentBack && status === 'draft') h += '<p class="tn-err">Sent back by ' + esc(run.sentBack.by) + ': ' + esc(run.sentBack.note || '') + '</p>';
      if (run.approval) h += '<p class="tn-sub">Approved by ' + esc(run.approval.by) + ' on ' + mdY(run.approval.at) + (run.approval.stillMatches === false ? ' <span class="tn-badword">(a figure changed after this)</span>' : '') + '</p>';
      h += '<p class="tn-sub">Prepared by ' + esc(run.preparedBy || '') + (run.submittedBy ? ' &middot; filed by ' + esc(run.submittedBy) + ' on ' + mdY(run.submittedAt) : '') + '</p>';
    }
    if (status === 'draft' || status === 'awaiting-ryan') {
      h += '<div class="tn-actions">'
        + '<label class="tn-sub tn-inline">Return <select class="tn-in" data-f="rt" aria-label="Return type">' + opt(RETURN_TYPES, run ? run.returnType : (p.kind === 'use' ? 'use' : 'sales')) + '</select></label>'
        + numIn('gross', run && run.grossSales, 'Gross sales') + numIn('taxable', run && run.taxableSales, 'Taxable') + numIn('exempt', run && run.exemptSales, 'Exempt') + numIn('taxdue', run && run.taxDue, 'Tax due')
        + '</div><div class="tn-actions">'
        + numIn('pamt', run && run.payment && run.payment.amount, 'Pay')
        + '<label class="tn-sub tn-inline">by <select class="tn-in" data-f="pmeth" aria-label="Payment method">' + opt(PAY_METHODS, run && run.payment ? run.payment.method : '') + '</select></label>'
        + '<label class="tn-sub tn-inline">on <input class="tn-in" type="date" data-f="pdate" value="' + esc(run && run.payment && run.payment.date || '') + '" aria-label="Payment date"></label>'
        + '<input class="tn-in tn-in--note" maxlength="300" data-f="rnote" placeholder="Note (optional)" value="' + esc(run && run.note || '') + '" aria-label="Note">'
        + '</div><div class="tn-actions">'
        + '<button type="button" class="th-btn th-btn--sm" data-tf="run-save" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Save draft</button>'
        + (isRyan() ? '' : '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="run-ready" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Save and send to Ryan</button>')
        + (isRyan() && run ? '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="run-approve" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Approve these figures</button>'
          + '<button type="button" class="th-btn th-btn--sm" data-tf="run-sendback" data-key="' + esc(key) + '">Send back</button>' : '')
        + '<button type="button" class="th-btn th-btn--sm" data-tf="run-close">Close</button>'
        + '</div>'
        + (isRyan() && !run ? '<p class="tn-sub">Save the draft first, then approve it.</p>' : '')
        + (isRyan() && run && status === 'draft' ? '<p class="tn-sub">Save any change first: Approve approves what is saved.</p>' : '');
    } else if (status === 'approved' || status === 'submitted') {
      h += '<p class="tn-muted">File and pay this return on the state\'s site exactly as approved, then record it here.</p>'
        + '<div class="tn-actions">'
        + '<input class="tn-in" maxlength="40" data-f="conf" placeholder="State confirmation number" aria-label="Confirmation number">'
        + '<label class="tn-sub tn-inline">paid on <input class="tn-in" type="date" data-f="pdate" value="' + esc(run && run.payment && run.payment.date || '') + '" aria-label="Payment date"></label>'
        + '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="run-confirm" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>' + (status === 'approved' ? 'Record it as filed' : 'Save confirmation number') + '</button>'
        + (isRyan() && status === 'approved' ? '<button type="button" class="th-btn th-btn--sm" data-tf="run-sendback" data-key="' + esc(key) + '">Send back</button>' : '')
        + '<button type="button" class="th-btn th-btn--sm" data-tf="run-close">Close</button>'
        + '</div>'
        + uploadBlock(p, key, run); // CUSTOM_TAXUPLOADFILE_BY_CLAUDE
    } else {
      h += '<p class="tn-okword">Confirmed' + (run.confirmation ? ': ' + esc(run.confirmation) : (run.confirmationLast4 ? ': &bull;&bull;' + esc(run.confirmationLast4) : '')) + '</p>'
        + '<div class="tn-actions"><button type="button" class="th-btn th-btn--sm" data-tf="run-close">Close</button></div>'
        + uploadBlock(p, key, run); // CUSTOM_TAXUPLOADFILE_BY_CLAUDE
    }
    return h + '</td></tr>';
  }
  /* END CUSTOM_TAXFILINGRUN_BY_CLAUDE */

  /* CUSTOM_TAXFILINGGUIDE_BY_CLAUDE (KAREN 3b004e9511cb, Ryan 2026-10-17) - "How to file & pay", opened in place under a
     return's row: the state's login link, the account numbers (whole for Ryan, last 4 for everybody else), the numbers for
     this return (the filing run once Ryan approved it, else that period's worksheet), the steps, a video and the yearly
     filings. Karen keeps the steps; Ryan edits them and alone sets the numbers. */
  function money2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
  function guideFor(stateCode) { return st.guides && st.guides.byState ? st.guides.byState[stateCode] || null : null; }
  function amountRows(p, key) {
    var run = p.run;
    if (run && (run.status === 'approved' || run.status === 'submitted' || run.status === 'confirmed')) {
      var rr = [['Gross sales', run.grossSales], ['Taxable sales', run.taxableSales], ['Exempt sales', run.exemptSales], ['Tax due', run.taxDue]];
      if (run.payment && run.payment.amount != null) rr.push(['Pay', run.payment.amount]);
      return { from: 'the filing run Ryan approved', rows: rr };
    }
    if (!p.period) return { error: 'No return is owed for ' + p.state + ' yet (set how often it files above), so there are no numbers to enter.' };
    var w = st.ws[key];
    if (!w || w.loading || w.error) return w || null;
    var t = w.totals || {}, tx = w.taxed || {};
    return {
      from: 'that period\'s worksheet (' + (t.orderCount || 0) + ' order' + (t.orderCount === 1 ? '' : 's') + (w.complete ? '' : ', still running through ' + mdY(w.throughDate)) + ')'
        + (run ? '. The filing run is not approved yet' : ''),
      rows: [['Total ' + p.state + ' sales', money2(t.grandTotal - t.taxes)], ['Taxable sales', money2(tx.grandTotal - tx.taxes)], ['Tax collected', money2(t.taxes)]]
    };
  }
  function copyBtn(val) { return '<button type="button" class="th-btn th-btn--sm" data-tf="guide-copy" data-val="' + esc(val) + '">Copy</button>'; }
  function guidePanel(p, key, colspan) {
    var gs = st.guides || {};
    var h = '<tr class="tn-row--edit" data-tf-guide="' + esc(key) + '"><td colspan="' + colspan + '"><div class="tf-guide">';
    if (gs.loading) return h + '<p class="tn-muted">Loading the guide&hellip;</p></div></td></tr>';
    if (gs.error) return h + '<p class="tn-err">' + esc(gs.error) + '</p></div></td></tr>';
    var g = guideFor(p.state) || { state: p.state, steps: [], accounts: [], blank: true };
    var siteName = g.site ? String(g.site).replace(/ \(.*\)$/, '') : p.state + ' tax site';
    h += '<div class="tn-actions">'
      + (g.loginUrl ? '<a class="th-btn th-btn--primary th-btn--sm" href="' + esc(g.loginUrl) + '" target="_blank" rel="noopener noreferrer">Open ' + esc(siteName) + ' &#8599;</a>' : '<span class="tn-muted">No login link on file yet.</span>');
    (g.accounts || []).forEach(function (a) {
      h += '<span class="tf-acct">' + esc(a.label) + ' <strong>' + (a.number ? esc(a.number) : '&bull;&bull;' + esc(a.last4 || '')) + '</strong>' + (a.number ? ' ' + copyBtn(a.number) : '') + '</span>';
    });
    if (isRyan() && st.acctEdit !== key) h += '<button type="button" class="th-btn th-btn--sm" data-tf="acct-edit" data-key="' + esc(key) + '">' + ((g.accounts || []).length ? 'Edit numbers' : 'Add account numbers') + '</button>';
    h += '</div>';
    if (st.acctEdit === key) {
      h += '<p class="tn-sub">One per line, label then a colon then the number (for example: Taxpayer number: 32012345678). Only you see the full numbers; Karen and everyone else see the last 4.</p>'
        + '<textarea class="tn-in tf-guide__ta" rows="3" data-f="g-accts" aria-label="Account numbers">' + esc((g.accounts || []).map(function (a) { return a.label + ': ' + (a.number || ''); }).join('\n')) + '</textarea>'
        + '<div class="tn-actions"><button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="acct-save" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Save numbers</button>'
        + '<button type="button" class="th-btn th-btn--sm" data-tf="acct-cancel">Cancel</button></div>';
    }
    var am = amountRows(p, key);
    h += '<p class="tf-guide__h">Numbers for this return</p>';
    if (!am || am.loading) h += '<p class="tn-muted">Reading the worksheet&hellip;</p>';
    else if (am.error) h += '<p class="tn-err">' + esc(am.error) + '</p>';
    else {
      h += '<table class="tn-table tf-guide__nums"><tbody>';
      am.rows.forEach(function (r) { h += '<tr><td>' + esc(r[0]) + '</td><td class="tn-num">' + usd(r[1]) + '</td><td>' + copyBtn(money2(r[1]).toFixed(2)) + '</td></tr>'; });
      h += '</tbody></table><p class="tn-sub">From ' + esc(am.from) + '.</p>';
    }
    if (st.guideEdit === key) {
      h += '<p class="tf-guide__h">Edit the steps</p>'
        + '<p class="tn-sub">One step per line. Account numbers never go here (use the numbers above).</p>'
        + '<textarea class="tn-in tf-guide__ta" rows="9" data-f="g-steps" aria-label="Steps">' + esc((g.steps || []).join('\n')) + '</textarea>'
        + '<input class="tn-in tn-in--note" data-f="g-video" placeholder="Video link (optional)" value="' + esc(g.videoUrl || '') + '" aria-label="Video link">'
        + '<textarea class="tn-in tf-guide__ta" rows="2" data-f="g-yearly" placeholder="Yearly filings not on the calendar (optional)" aria-label="Yearly filings">' + esc(g.yearly || '') + '</textarea>'
        + '<textarea class="tn-in tf-guide__ta" rows="2" data-f="g-notes" placeholder="Notes (optional)" aria-label="Notes">' + esc(g.notes || '') + '</textarea>'
        + '<div class="tn-actions"><button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="guide-save" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>Save steps</button>'
        + '<button type="button" class="th-btn th-btn--sm" data-tf="guide-cancel">Cancel</button></div>';
    } else {
      h += '<p class="tf-guide__h">Steps</p>';
      if ((g.steps || []).length) {
        h += '<ol class="tf-guide__steps">';
        g.steps.forEach(function (s) { h += '<li>' + esc(s) + '</li>'; });
        h += '</ol>';
      } else h += '<p class="tn-muted">No steps yet. They get written the first time ' + esc(p.state) + ' is filed: click Edit steps, or ask Karen.</p>';
      if (g.videoUrl) h += '<p><a href="' + esc(g.videoUrl) + '" target="_blank" rel="noopener noreferrer">&#9654; Video walkthrough</a></p>';
      if (g.notes) h += '<p class="tn-sub">' + esc(g.notes) + '</p>';
      if (g.yearly) h += '<p class="tn-sub"><strong>Once a year:</strong> ' + esc(g.yearly) + '</p>';
      h += '<div class="tn-actions"><span class="tn-sub">' + (g.seeded ? 'Starter steps from Ryan\'s notes.' : g.updatedBy ? 'Last edited by ' + esc(g.updatedBy) + ', ' + mdY(g.updatedAt) : '') + '</span>'
        + '<button type="button" class="th-btn th-btn--sm" data-tf="guide-edit" data-key="' + esc(key) + '">Edit steps</button>'
        + (st.marking === key || p.status === 'filed' || key.slice(-2) === '|s' ? '' : '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="mark" data-key="' + esc(key) + '">Record it as filed</button>')
        + '<button type="button" class="th-btn th-btn--sm" data-tf="guide-close">Close</button></div>';
    }
    return h + '</div></td></tr>';
  }
  /* END CUSTOM_TAXFILINGGUIDE_BY_CLAUDE */

  function scheduleRow(s) {
    var e = st.edits[s.state] || null;
    var freq = e ? e.frequency : (s.frequency || '');
    var day = e ? e.dueDay : s.dueDay;
    var opts = FREQ.map(function (f) { return '<option value="' + f[0] + '"' + (freq === f[0] ? ' selected' : '') + '>' + f[1] + '</option>'; }).join('');
    var next = (s.periods || []).filter(function (p) { return p.status !== 'filed'; })[0];
    return '<tr data-tf-state="' + esc(s.state) + '">'
      + '<td><strong>' + esc(s.state) + '</strong><span class="tn-sub">' + esc(s.kind === 'use' ? 'Use tax permit' : 'Sales tax permit') + (s.last4 ? ' &hellip;' + esc(s.last4) : '') + '</span></td>'
      + '<td><select class="tn-in" data-f="frequency" aria-label="How often ' + esc(s.state) + ' files">' + opts + '</select>'
      + (s.setBy ? '<span class="tn-sub">set by ' + esc(s.setBy) + '</span>' : '') + '</td>'
      + '<td><input class="tn-in tn-in--st" type="number" min="0" max="31" data-f="dueDay" value="' + esc(day) + '" aria-label="Due day for ' + esc(s.state) + '">'
      + '<span class="tn-sub">' + (Number(day) === 0 ? 'last day of the month after' : 'of the month after') + (s.dueDayIsDefault && !e ? ' (usual)' : '') + '</span></td>'
      + '<td>' + (next ? esc(next.label) + '<span class="tn-sub">due ' + mdY(next.due) + '</span>' : (s.frequency ? '<span class="tn-muted">Nothing owed</span>' : '<span class="tn-muted">Set how often first</span>')) + '</td>'
      + '<td>' + (next ? statusPill(next) : '') + '</td>'
      + '<td>' + (e ? '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="save-sched" data-state="' + esc(s.state) + '"' + (st.busy ? ' disabled' : '') + '>Save</button> '
        + '<button type="button" class="th-btn th-btn--sm" data-tf="cancel-sched" data-state="' + esc(s.state) + '">Cancel</button>'
        : '<button type="button" class="th-btn th-btn--sm" data-tf="guide-open" data-key="' + esc(stateGuideKey(s, next)) + '">How to file &amp; pay</button>') + '</td>' // CUSTOM_TAXFILINGGUIDE_BY_CLAUDE
      + '</tr>'
      + (st.guideOpen === stateGuideKey(s, next) ? guidePanel(next || { state: s.state, period: '', status: 'none' }, stateGuideKey(s, next), 6) : '');
  }
  /* CUSTOM_TAXFILINGGUIDE_BY_CLAUDE - the guide is a STATE's, so each state row opens it too (a state with no schedule
     yet has no return owed, and the button must not hide behind one). Its key ends "|s" so it never opens the owed row's. */
  function stateGuideKey(s, next) { return s.state + '|' + (next ? next.period : '') + '|s'; }

  function owedRow(p) {
    var key = p.state + '|' + p.period;
    var cls = p.status === 'overdue' ? 'tn-row--red' : p.status === 'due-soon' ? 'tn-row--amber' : '';
    var h = '<tr class="' + cls + '">'
      + '<td><strong>' + esc(p.state) + '</strong></td>'
      + '<td>' + esc(p.label) + '<span class="tn-sub">' + mdY(p.from) + ' to ' + mdY(p.to) + '</span></td>'
      + '<td>' + mdY(p.due) + '</td>'
      + '<td>' + statusPill(p) + '</td>'
      + '<td>' + runPill(p.run) + ' <button type="button" class="th-btn th-btn--sm" data-tf="run-open" data-key="' + esc(key) + '">' + (p.run ? 'Return' : 'Prepare') + '</button></td>' // CUSTOM_TAXFILINGRUN_BY_CLAUDE
      + '<td><button type="button" class="th-btn th-btn--sm" data-tf="guide-open" data-key="' + esc(key) + '">How to file &amp; pay</button> ' // CUSTOM_TAXFILINGGUIDE_BY_CLAUDE
      + '<button type="button" class="th-btn th-btn--sm" data-tf="open" data-state="' + esc(p.state) + '" data-from="' + esc(p.from) + '" data-to="' + esc(p.to) + '">Open numbers</button> '
      + (st.marking === key ? '' : '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="mark" data-key="' + esc(key) + '">Mark filed</button>') + '</td>'
      + '</tr>';
    if (st.guideOpen === key) h += guidePanel(p, key, 6); // CUSTOM_TAXFILINGGUIDE_BY_CLAUDE
    if (st.runOpen === key) h += runPanel(p, key, 6); // CUSTOM_TAXFILINGRUN_BY_CLAUDE
    if (st.marking === key) {
      h += '<tr class="tn-row--edit" data-tf-mark="' + esc(key) + '"><td colspan="6">'
        + '<p class="tn-muted">This freezes ' + esc(p.state) + '\'s numbers for ' + esc(p.label) + ' as they are right now, and stops the reminders for it. Only Ryan can unlock it later.</p>'
        + '<div class="tn-actions">'
        + '<label class="tn-sub tn-inline">Amount paid $ <input class="tn-in" type="number" min="0" step="0.01" data-f="amountPaid" aria-label="Amount paid"></label>'
        + '<input class="tn-in" maxlength="40" data-f="confirmation" placeholder="State confirmation number" value="' + esc(p.run && p.run.confirmation || '') + '" aria-label="Confirmation number">' // CUSTOM_TAXFILINGRUN_BY_CLAUDE
        + '<input class="tn-in tn-in--note" maxlength="300" data-f="note" placeholder="Note (optional): who filed it" aria-label="Note">'
        + '<button type="button" class="th-btn th-btn--primary th-btn--sm" data-tf="confirm-mark" data-key="' + esc(key) + '"' + (st.busy ? ' disabled' : '') + '>' + (st.busy ? 'Freezing&hellip;' : 'Confirm: mark filed') + '</button>'
        + '<button type="button" class="th-btn th-btn--sm" data-tf="cancel-mark">Cancel</button>'
        + '</div></td></tr>';
    }
    return h;
  }

  function filedRow(f) {
    var t = f.totals || {};
    var key = f.state + '|' + f.period;
    var h = '<tr>'
      + '<td><strong>' + esc(f.state) + '</strong></td>'
      + '<td>' + esc(f.label || f.period) + '</td>'
      + '<td>' + mdY(f.filedAt) + '<span class="tn-sub">by ' + esc(f.filedBy || '') + '</span></td>'
      + '<td class="tn-num">' + (f.amountPaid != null ? usd(f.amountPaid) : '<span class="tn-muted">&mdash;</span>') + '</td>'
      + '<td class="tn-num">' + usd(t.grandTotal) + '<span class="tn-sub">' + (t.orderCount || 0) + ' order' + (t.orderCount === 1 ? '' : 's') + ', ' + usd(t.taxes) + ' tax</span></td>'
      + '<td>' + (f.locked ? '<span class="tn-pill tn-pill--ok">Locked</span>' : '<span class="tn-pill tn-pill--warn">Unlocked</span>') + '</td>'
      + '<td>' + confCell(f) + (f.run ? ' <button type="button" class="th-btn th-btn--sm" data-tf="run-open" data-key="' + esc(key) + '">Return</button>' : '') + '</td>' // CUSTOM_TAXFILINGRUN_BY_CLAUDE
      + '<td>' + proofCell(f) + '</td>' // CUSTOM_TAXFILINGPROOF_BY_CLAUDE
      + '<td><button type="button" class="th-btn th-btn--sm" data-tf="open" data-state="' + esc(f.state) + '" data-from="' + esc(f.from) + '" data-to="' + esc(f.to) + '">Open numbers</button> '
      + (f.locked && isRyan() ? '<button type="button" class="th-btn th-btn--sm" data-tf="unlock" data-state="' + esc(f.state) + '" data-period="' + esc(f.period) + '">Unlock</button>' : '')
      + '</td></tr>';
    if (st.runOpen === key && f.run) h += runPanel(f, key, 9);
    if (st.proofOpen === key) h += proofPanel(f, key, 9); // CUSTOM_TAXFILINGPROOF_BY_CLAUDE
    return h;
  }

  function card() {
    var c = collapsed();
    var peek, body;
    if (st.calErr) { peek = 'failed to load'; body = '<p class="tn-err">' + esc(st.calErr) + '</p>'; }
    else if (!st.cal) { peek = 'loading&hellip;'; body = '<p class="tn-muted">Reading the calendar&hellip;</p>'; }
    else {
      var d = st.cal;
      var owed = [];
      (d.states || []).forEach(function (s) { (s.periods || []).forEach(function (p) { if (p.status !== 'filed') owed.push(p); }); });
      owed.sort(function (a, b) { return a.due < b.due ? -1 : a.due > b.due ? 1 : a.state < b.state ? -1 : 1; });
      var overdue = owed.filter(function (p) { return p.status === 'overdue'; }).length;
      var soon = owed.filter(function (p) { return p.status === 'due-soon'; }).length;
      var unset = (d.states || []).filter(function (s) { return !s.frequency; }).length;
      var waitingRyan = (d.runs || []).filter(function (r) { return r.status === 'awaiting-ryan'; }).length; // CUSTOM_TAXFILINGRUN_BY_CLAUDE
      var noConf = (d.filed || []).filter(function (f) { return f.needsConfirmation; }).length;
      var rem = st.cfg && st.cfg.config && st.cfg.config.remindersEnabled;
      peek = owed.length + ' return' + (owed.length === 1 ? '' : 's') + ' owed'
        + (overdue ? ' &middot; <span class="tn-badword">' + overdue + ' overdue</span>' : '')
        + (soon ? ' &middot; <span class="tn-warnword">' + soon + ' due soon</span>' : '')
        + (unset ? ' &middot; ' + unset + ' not set' : '')
        + (waitingRyan ? ' &middot; <span class="tn-warnword">' + waitingRyan + ' waiting on Ryan</span>' : '')
        + (noConf ? ' &middot; <span class="tn-warnword">' + noConf + ' with no confirmation</span>' : '')
        + ' &middot; reminders ' + (rem ? '<span class="tn-okword">ON</span>' : 'OFF');

      body = '<p class="tn-muted">How often each state we are registered in has us file, and the returns owed. Set how often a state files (it is on the state\'s registration letter or its tax website); the due day starts at the state\'s usual day, so check it. Once a return is filed on the state\'s site, Mark filed freezes that state\'s numbers for the period.</p>';
      body += '<div class="tn-tablewrap"><table class="tn-table tn-table--edit"><thead><tr><th>State</th><th>How often</th><th>Due day</th><th>Next return</th><th>Status</th><th></th></tr></thead><tbody>';
      if (!(d.states || []).length) body += '<tr><td colspan="6" class="tn-muted">No active sales or use registrations on the Tax Hub yet (the Registrations card above).</td></tr>';
      (d.states || []).forEach(function (s) { body += scheduleRow(s); });
      body += '</tbody></table></div>';

      body += '<h3 class="tf-sub">Returns owed</h3>';
      body += '<div class="tn-tablewrap"><table class="tn-table"><thead><tr><th>State</th><th>Period</th><th>Due</th><th>Status</th><th>Return</th><th></th></tr></thead><tbody>';
      if (!owed.length) body += '<tr><td colspan="6" class="tn-muted">Nothing owed right now.</td></tr>';
      owed.forEach(function (p) { body += owedRow(p); });
      body += '</tbody></table></div>';

      var filed = d.filed || [];
      if (filed.length) {
        body += '<h3 class="tf-sub">Filed</h3>';
        body += '<div class="tn-tablewrap"><table class="tn-table"><thead><tr><th>State</th><th>Period</th><th>Filed</th><th class="tn-num">Paid</th><th class="tn-num">Frozen sales</th><th></th><th>Confirmation</th><th>Proof</th><th></th></tr></thead><tbody>';
        filed.slice(0, 12).forEach(function (f) { body += filedRow(f); });
        body += '</tbody></table></div>';
      }
      if (st.msg) body += '<div class="tn-actions"><span class="' + (st.msgErr ? 'tn-err' : 'tn-okword') + '">' + esc(st.msg) + '</span></div>';

      var cf = st.cfg && st.cfg.config;
      body += '<label class="tn-check"><input type="checkbox" data-tf="reminders"' + (cf && cf.remindersEnabled ? ' checked' : '') + (st.cfg ? '' : ' disabled') + '> '
        + '<span><strong>Filing reminders</strong>: email Ryan and tell Karen a week before each return is due, the day before, and once a week while it is overdue, until the period is marked filed.</span></label>';
      if (st.cfgErr) body += '<p class="tn-err">' + esc(st.cfgErr) + '</p>';
      var last = st.cfg && st.cfg.lastRun;
      if (last) body += '<p class="tn-muted">Last reminder check: ' + esc(String(last.at).slice(0, 16).replace('T', ' ')) + ' UTC'
        + ((last.due || []).length ? ' &middot; ' + last.due.length + ' reminder' + (last.due.length === 1 ? '' : 's') + (last.email && last.email.sent ? ', emailed' : last.email ? ', email FAILED' : '') : ' &middot; nothing due') + '.</p>';
    }
    return '<section class="tn-card' + (c ? ' tn-card--collapsed' : '') + '" data-tn-card="filing">'
      + '<div class="tn-card__head" role="button" tabindex="0" aria-expanded="' + (c ? 'false' : 'true') + '">'
      + '<h2 class="tn-card__title">Filing calendar</h2>'
      + '<span class="tn-card__peek">' + peek + '</span>'
      + '<span class="tn-card__chev" aria-hidden="true">' + (c ? '+' : '&minus;') + '</span>'
      + '</div><div class="tn-card__body">' + body + '</div></section>';
  }

  function render() { var el = rootEl(); if (el) el.innerHTML = card(); }

  function rowValues(stateCode) {
    var tr = rootEl() && rootEl().querySelector('[data-tf-state="' + stateCode + '"]');
    if (!tr) return null;
    var f = tr.querySelector('[data-f="frequency"]'), d = tr.querySelector('[data-f="dueDay"]');
    return { frequency: f ? f.value : '', dueDay: d ? d.value : '' };
  }
  function saveSchedule(stateCode) {
    var v = rowValues(stateCode);
    if (!v) return;
    st.busy = true; st.msg = ''; render();
    var row = { state: stateCode, frequency: v.frequency || null };
    if (v.dueDay !== '') row.dueDay = Number(v.dueDay);
    api('/tax-filing/schedule', { method: 'POST', body: JSON.stringify({ rows: [row] }) }).then(function () {
      st.busy = false; delete st.edits[stateCode]; st.msgErr = false; st.msg = stateCode + ' saved.';
      loadCal();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = 'Save failed: ' + errOf(err); render(); });
  }
  function confirmMark(key) {
    var tr = rootEl() && rootEl().querySelector('[data-tf-mark="' + key + '"]');
    var amt = tr && tr.querySelector('[data-f="amountPaid"]'), note = tr && tr.querySelector('[data-f="note"]');
    var conf = tr && tr.querySelector('[data-f="confirmation"]'); // CUSTOM_TAXFILINGRUN_BY_CLAUDE
    var parts = key.split('|');
    var body = { state: parts[0], period: parts[1] };
    if (amt && amt.value !== '') body.amountPaid = Number(amt.value);
    if (conf && conf.value.trim()) body.confirmation = conf.value.trim();
    if (note && note.value.trim()) body.note = note.value.trim();
    st.busy = true; st.msg = ''; render();
    api('/admin/tax-filing/mark', { method: 'POST', body: JSON.stringify(body) }).then(function (res) {
      st.busy = false; st.marking = null; st.msgErr = false;
      var t = (res.filed && res.filed.totals) || {};
      st.msg = parts[0] + ' ' + ((res.filed && res.filed.label) || parts[1]) + ' marked filed and frozen (' + (t.orderCount || 0) + ' orders).';
      delete st.snaps[key];
      loadCal();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = 'Mark filed failed: ' + errOf(err); render(); });
  }
  function unlock(stateCode, period) {
    var reason = window.prompt('Why unlock ' + stateCode + ' ' + period + '? (for example: amended return). It stays on the record.');
    if (reason == null) return;
    reason = reason.trim();
    if (reason.length < 5) { st.msgErr = true; st.msg = 'Unlock needs a reason (5 characters or more).'; render(); return; }
    api('/admin/tax-filing/unlock', { method: 'POST', body: JSON.stringify({ state: stateCode, period: period, reason: reason }) }).then(function () {
      st.msgErr = false; st.msg = stateCode + ' ' + period + ' unlocked. Its numbers are live again until it is marked filed.';
      delete st.snaps[stateCode + '|' + period];
      loadCal();
    }).catch(function (err) { st.msgErr = true; st.msg = 'Unlock failed: ' + errOf(err); render(); });
  }
  /* CUSTOM_TAXFILINGRUN_BY_CLAUDE - the filing-run actions. */
  function runVal(key, f) {
    var tr = rootEl() && rootEl().querySelector('[data-tf-run="' + key + '"]');
    var el = tr && tr.querySelector('[data-f="' + f + '"]');
    return el ? String(el.value || '').trim() : '';
  }
  function runCall(path, body, okMsg) {
    st.busy = true; st.msg = ''; render();
    return api(path, { method: 'POST', body: JSON.stringify(body) }).then(function (res) {
      st.busy = false; st.msgErr = false; st.msg = okMsg(res); loadCal();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = errOf(err); render(); });
  }
  function saveRun(key, ready) {
    var parts = key.split('|');
    var body = { state: parts[0], period: parts[1], returnType: runVal(key, 'rt') || undefined,
      grossSales: runVal(key, 'gross'), taxableSales: runVal(key, 'taxable'), exemptSales: runVal(key, 'exempt'), taxDue: runVal(key, 'taxdue'),
      note: runVal(key, 'rnote') };
    var pay = {};
    if (runVal(key, 'pamt') !== '') pay.amount = runVal(key, 'pamt');
    if (runVal(key, 'pmeth')) pay.method = runVal(key, 'pmeth');
    if (runVal(key, 'pdate')) pay.date = runVal(key, 'pdate');
    body.payment = (pay.amount != null || pay.method || pay.date) ? pay : null;
    if (ready) body.ready = true;
    runCall('/tax-filing/run', body, function (res) { return parts[0] + ' ' + parts[1] + (res.run && res.run.status === 'awaiting-ryan' ? ' sent to Ryan for approval.' : ' saved as a draft.'); });
  }
  function decideRun(key, decision) {
    var parts = key.split('|');
    var body = { state: parts[0], period: parts[1], decision: decision };
    if (decision === 'send-back') {
      var why = window.prompt('What should be fixed before you approve ' + parts[0] + ' ' + parts[1] + '?');
      if (why == null) return;
      body.note = why.trim();
      if (body.note.length < 5) { st.msgErr = true; st.msg = 'Say what to fix (5 characters or more).'; render(); return; }
    }
    runCall('/tax-filing/run/approve', body, function () { return parts[0] + ' ' + parts[1] + (decision === 'approve' ? ' approved. It may now be filed on the state\'s site.' : ' sent back.'); });
  }
  function confirmRun(key) {
    var parts = key.split('|');
    var body = { state: parts[0], period: parts[1] };
    if (runVal(key, 'conf')) body.confirmation = runVal(key, 'conf');
    if (runVal(key, 'pdate')) body.payment = { date: runVal(key, 'pdate') };
    runCall('/tax-filing/run/confirm', body, function (res) { return parts[0] + ' ' + parts[1] + ' recorded as ' + ((res.run && res.run.status) || 'submitted') + '.'; });
  }
  function addConfirmation(stateCode, period) {
    var c = window.prompt('The state\'s confirmation number for ' + stateCode + ' ' + period + ':');
    if (c == null || !c.trim()) return;
    runCall('/admin/tax-filing/confirmation', { state: stateCode, period: period, confirmation: c.trim() }, function () { return stateCode + ' ' + period + ' confirmation saved.'; });
  }
  /* CUSTOM_TAXFILINGPROOF_BY_CLAUDE (KAREN cdd12ba998e6) - the proof list, an upload, an authenticated open, a removal. */
  function proofQuery(key) { var p = key.split('|'); return '?state=' + encodeURIComponent(p[0]) + '&period=' + encodeURIComponent(p[1]); }
  function loadProof(key) {
    st.proofs[key] = { loading: true };
    api('/admin/tax-filing/proof' + proofQuery(key)).then(function (d) { st.proofs[key] = { items: d.items || [] }; render(); })
      .catch(function (e) { st.proofs[key] = { error: errOf(e) }; render(); });
  }
  function attachProof(key) {
    var tr = document.querySelector('[data-tf-proof="' + key + '"]');
    var input = tr && tr.querySelector('[data-f="proof-file"]');
    var kindEl = tr && tr.querySelector('[data-f="proof-kind"]');
    var file = input && input.files && input.files[0];
    if (!file) { st.msg = 'Choose a PDF, PNG or JPG first.'; st.msgErr = true; render(); return; }
    if (file.size > 5 * 1024 * 1024) { st.msg = 'That file is over 5 MB.'; st.msgErr = true; render(); return; }
    var parts = key.split('|');
    var reader = new FileReader();
    reader.onload = function () {
      var b64 = String(reader.result || '').split(',')[1] || '';
      st.busy = true; render();
      api('/tax-filing/proof', { method: 'POST', body: JSON.stringify({ state: parts[0], period: parts[1], source: 'file', kind: kindEl ? kindEl.value : 'confirmation', name: file.name, contentType: file.type, base64: b64 }) })
        .then(function () { st.busy = false; st.msg = file.name + ' attached to ' + parts[0] + ' ' + parts[1] + '.'; st.msgErr = false; loadProof(key); loadCal(); })
        .catch(function (e) { st.busy = false; st.msg = 'Attach failed: ' + errOf(e); st.msgErr = true; render(); });
    };
    reader.onerror = function () { st.msg = 'Could not read that file.'; st.msgErr = true; render(); };
    reader.readAsDataURL(file);
  }
  function openProof(key, id) {
    var auth = window.RepPortal && window.RepPortal.Auth;
    var tokP = auth && auth.getAccessToken ? auth.getAccessToken() : Promise.resolve((session() || {}).accessToken);
    var win = window.open('', '_blank');
    Promise.resolve(tokP).then(function (tok) {
      return fetch(WORKER_URL + '/admin/tax-filing/proof' + proofQuery(key) + '&id=' + encodeURIComponent(id), { headers: { Authorization: 'Bearer ' + tok } });
    }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
      .then(function (blob) { var u = URL.createObjectURL(blob); if (win) win.location.href = u; else window.location.href = u; })
      .catch(function (e) { if (win) win.close(); st.msg = 'Could not open it: ' + errOf(e); st.msgErr = true; render(); });
  }
  function removeProof(key, id) {
    if (!window.confirm('Remove this from the record? A removed file cannot be brought back.')) return;
    var parts = key.split('|');
    api('/admin/tax-filing/proof-delete', { method: 'POST', body: JSON.stringify({ state: parts[0], period: parts[1], id: id }) })
      .then(function () { st.msg = 'Removed.'; st.msgErr = false; loadProof(key); loadCal(); })
      .catch(function (e) { st.msg = 'Remove failed: ' + errOf(e); st.msgErr = true; render(); });
  }
  /* END CUSTOM_TAXFILINGRUN_BY_CLAUDE */

  /* CUSTOM_TAXFILINGGUIDE_BY_CLAUDE - the guide's reads and saves. */
  function loadGuides() {
    st.guides = { loading: true };
    api('/tax-filing/guide').then(function (d) {
      var by = {};
      (d.guides || []).forEach(function (g) { by[g.state] = g; });
      st.guides = { byState: by }; render();
    }).catch(function (e) { st.guides = { error: 'Could not load the guide: ' + errOf(e) }; render(); });
  }
  function loadWorksheet(key) {
    if (st.ws[key] && !st.ws[key].error) return;
    st.ws[key] = { loading: true };
    api('/tax-filing/worksheet' + proofQuery(key)).then(function (d) { st.ws[key] = d; render(); })
      .catch(function (e) { st.ws[key] = { error: 'Could not read the worksheet: ' + errOf(e) }; render(); });
  }
  function guideVal(key, f) {
    var tr = rootEl() && rootEl().querySelector('[data-tf-guide="' + key + '"]');
    var el = tr && tr.querySelector('[data-f="' + f + '"]');
    return el ? String(el.value || '') : '';
  }
  function saveGuide(key) {
    var stateCode = key.split('|')[0];
    var body = { state: stateCode,
      steps: guideVal(key, 'g-steps').split('\n').map(function (s) { return s.trim(); }).filter(Boolean),
      videoUrl: guideVal(key, 'g-video').trim(), yearly: guideVal(key, 'g-yearly').trim(), notes: guideVal(key, 'g-notes').trim() };
    st.busy = true; st.msg = ''; render();
    api('/tax-filing/guide', { method: 'POST', body: JSON.stringify(body) }).then(function (res) {
      st.busy = false; st.guideEdit = null; st.msgErr = false; st.msg = stateCode + ' steps saved.';
      if (res.guide && st.guides && st.guides.byState) st.guides.byState[stateCode] = res.guide;
      render();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = 'Save failed: ' + errOf(err); render(); });
  }
  function saveAccounts(key) {
    var stateCode = key.split('|')[0];
    var accounts = [];
    var bad = '';
    guideVal(key, 'g-accts').split('\n').forEach(function (line) {
      var t = line.trim();
      if (!t) return;
      var i = t.lastIndexOf(':');
      if (i < 1) { bad = bad || t; return; }
      accounts.push({ label: t.slice(0, i).trim(), number: t.slice(i + 1).trim() });
    });
    if (bad) { st.msgErr = true; st.msg = 'Each line needs a label, a colon, then the number ("' + bad + '").'; render(); return; }
    st.busy = true; st.msg = ''; render();
    api('/admin/tax-filing/guide-accounts', { method: 'POST', body: JSON.stringify({ state: stateCode, accounts: accounts }) }).then(function (res) {
      st.busy = false; st.acctEdit = null; st.msgErr = false; st.msg = stateCode + ' account numbers saved.';
      if (res.guide && st.guides && st.guides.byState) st.guides.byState[stateCode] = res.guide;
      render();
    }).catch(function (err) { st.busy = false; st.msgErr = true; st.msg = 'Save failed: ' + errOf(err); render(); });
  }
  function copyValue(v) {
    var done = function () { st.msgErr = false; st.msg = 'Copied ' + v + '.'; render(); };
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(v).then(done, function () { st.msg = 'Copy failed: select it by hand.'; st.msgErr = true; render(); }); return; }
    } catch (e) {}
    st.msgErr = true; st.msg = 'Copy is not available here: select it by hand.'; render();
  }
  /* END CUSTOM_TAXFILINGGUIDE_BY_CLAUDE */

  function setReminders(on) {
    api('/admin/tax-filing-config', { method: 'POST', body: JSON.stringify({ remindersEnabled: on }) }).then(function (res) {
      st.cfg = Object.assign({}, st.cfg, { config: res.config }); st.msgErr = false; st.msg = on ? 'Filing reminders are ON.' : 'Filing reminders are OFF.'; render();
    }).catch(function (err) { st.msgErr = true; st.msg = 'Save failed: ' + errOf(err); render(); });
  }

  function onClick(e) {
    var head = e.target.closest('.tn-card__head');
    if (head) {
      var cardEl = head.parentElement;
      var now = cardEl.classList.toggle('tn-card--collapsed'); setCollapsed(now);
      head.setAttribute('aria-expanded', now ? 'false' : 'true');
      head.querySelector('.tn-card__chev').innerHTML = now ? '+' : '&minus;';
      return;
    }
    var b = e.target.closest('[data-tf]');
    if (!b) return;
    var what = b.getAttribute('data-tf');
    if (what === 'save-sched') saveSchedule(b.getAttribute('data-state'));
    else if (what === 'cancel-sched') { delete st.edits[b.getAttribute('data-state')]; render(); }
    else if (what === 'mark') { st.marking = b.getAttribute('data-key'); st.msg = ''; render(); }
    else if (what === 'cancel-mark') { st.marking = null; render(); }
    else if (what === 'confirm-mark') confirmMark(b.getAttribute('data-key'));
    else if (what === 'unlock') unlock(b.getAttribute('data-state'), b.getAttribute('data-period'));
    else if (what === 'run-open') { var rk = b.getAttribute('data-key'); st.runOpen = st.runOpen === rk ? null : rk; st.msg = ''; render(); } // CUSTOM_TAXFILINGRUN_BY_CLAUDE
    else if (what === 'run-close') { st.runOpen = null; render(); }
    else if (what === 'run-save') saveRun(b.getAttribute('data-key'), false);
    else if (what === 'run-ready') saveRun(b.getAttribute('data-key'), true);
    else if (what === 'run-approve') decideRun(b.getAttribute('data-key'), 'approve');
    else if (what === 'run-sendback') decideRun(b.getAttribute('data-key'), 'send-back');
    else if (what === 'run-confirm') confirmRun(b.getAttribute('data-key'));
    else if (what === 'upload-file') downloadUploadFile(b.getAttribute('data-key')); // CUSTOM_TAXUPLOADFILE_BY_CLAUDE
    else if (what === 'add-conf') addConfirmation(b.getAttribute('data-state'), b.getAttribute('data-period'));
    else if (what === 'proof-open') { var pk = b.getAttribute('data-key'); st.proofOpen = st.proofOpen === pk ? null : pk; st.msg = ''; if (st.proofOpen) loadProof(pk); render(); } // CUSTOM_TAXFILINGPROOF_BY_CLAUDE
    else if (what === 'proof-close') { st.proofOpen = null; render(); }
    else if (what === 'guide-open') { // CUSTOM_TAXFILINGGUIDE_BY_CLAUDE
      var gk = b.getAttribute('data-key'); st.guideOpen = st.guideOpen === gk ? null : gk; st.guideEdit = null; st.acctEdit = null; st.msg = '';
      if (st.guideOpen) { if (!st.guides || st.guides.error) loadGuides(); if (gk.split('|')[1]) loadWorksheet(gk); }
      render();
    }
    else if (what === 'guide-close') { st.guideOpen = null; st.guideEdit = null; st.acctEdit = null; render(); }
    else if (what === 'guide-edit') { st.guideEdit = b.getAttribute('data-key'); render(); }
    else if (what === 'guide-cancel') { st.guideEdit = null; render(); }
    else if (what === 'guide-save') saveGuide(b.getAttribute('data-key'));
    else if (what === 'acct-edit') { st.acctEdit = b.getAttribute('data-key'); render(); }
    else if (what === 'acct-cancel') { st.acctEdit = null; render(); }
    else if (what === 'acct-save') saveAccounts(b.getAttribute('data-key'));
    else if (what === 'guide-copy') copyValue(b.getAttribute('data-val'));
    else if (what === 'proof-attach') attachProof(b.getAttribute('data-key'));
    else if (what === 'proof-view') openProof(b.getAttribute('data-key'), b.getAttribute('data-id'));
    else if (what === 'proof-remove') removeProof(b.getAttribute('data-key'), b.getAttribute('data-id'));
    else if (what === 'open') {
      if (window.TaxHub && typeof window.TaxHub.show === 'function') window.TaxHub.show(b.getAttribute('data-state'), b.getAttribute('data-from'), b.getAttribute('data-to'));
    }
  }
  function onChange(e) {
    if (e.target.closest('[data-tf="reminders"]')) { setReminders(e.target.checked); return; }
    var tr = e.target.closest('[data-tf-state]');
    if (tr && e.target.getAttribute('data-f')) {
      var code = tr.getAttribute('data-tf-state');
      st.edits[code] = rowValues(code);
      render();
    }
  }
  function onKey(e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.classList && e.target.classList.contains('tn-card__head')) { e.preventDefault(); e.target.click(); }
  }

  function refreshReport() { if (window.TaxHub && typeof window.TaxHub.render === 'function') { try { window.TaxHub.render(); } catch (e) {} } }
  function loadCal() {
    api('/tax-filing').then(function (d) { st.cal = d; st.calErr = null; render(); refreshReport(); })
      .catch(function (e) { st.calErr = errOf(e); render(); });
  }
  function loadCfg() { api('/admin/tax-filing-config').then(function (d) { st.cfg = d; st.cfgErr = null; render(); }).catch(function (e) { st.cfgErr = errOf(e); render(); }); }

  /** For the Sales-by-state report: the frozen rows of a LOCKED filed period matching exactly, else null. */
  function frozenFor(stateCode, from, to) {
    if (!st.cal || !st.cal.filed) return null;
    var f = null;
    for (var i = 0; i < st.cal.filed.length; i++) {
      var x = st.cal.filed[i];
      if (x.locked && x.state === stateCode && x.from === from && x.to === to) { f = x; break; }
    }
    if (!f) return null;
    var key = f.state + '|' + f.period;
    var snap = st.snaps[key];
    if (snap) return snap;
    st.snaps[key] = { loading: true };
    api('/admin/tax-filing/snapshot?state=' + encodeURIComponent(f.state) + '&period=' + encodeURIComponent(f.period)).then(function (res) {
      st.snaps[key] = { rows: (res.filed && res.filed.rows) || [], rec: res.filed || {} };
      refreshReport();
    }).catch(function (e) { st.snaps[key] = { error: errOf(e) }; refreshReport(); });
    return st.snaps[key];
  }

  var booted = false;
  function boot() {
    if (booted) return;
    var s = session();
    if (!s) return;
    booted = true;
    var el = rootEl();
    if (!el) return;
    if (!s.admin) { el.hidden = true; return; }
    el.addEventListener('click', onClick);
    el.addEventListener('change', onChange);
    el.addEventListener('keydown', onKey);
    render();
    loadCal(); loadCfg();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('rep-portal:authenticated', boot);
  window.TaxFiling = { state: st, frozenFor: frozenFor, reload: function () { loadCal(); loadCfg(); } };
})();
